import mitt, { Emitter } from "mitt";
import { useEffect, useRef } from "react";

/**
 * Admin teams event types
 */
type AdminTeamsEvents = {
  refreshTeams: void;
  coverageBatchStart: { teamIds: string[] };
  coverageBatchComplete: { results: Record<string, number | null> };
};

/**
 * Singleton event emitter for admin teams actions
 */
const adminTeamsEmitter: Emitter<AdminTeamsEvents> = mitt<AdminTeamsEvents>();

/**
 * Hook to emit admin teams events
 * Used by dialogs/cells after modifying a team
 */
export function useAdminTeamsEmitter() {
  /** Trigger a refresh of all teams data */
  const refreshTeams = () => {
    adminTeamsEmitter.emit("refreshTeams");
  };

  return { refreshTeams };
}

/**
 * Hook to listen for admin teams refresh events
 * Used by the teams list page to refetch data
 */
export function useAdminTeamsRefreshListener(onRefresh: () => void) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    const handler = () => {
      onRefreshRef.current();
    };

    adminTeamsEmitter.on("refreshTeams", handler);
    return () => {
      adminTeamsEmitter.off("refreshTeams", handler);
    };
  }, []);
}

/**
 * Hook to listen for coverage batch events in individual cells
 */
export function useCoverageBatchListener(
  teamId: string,
  onStart: () => void,
  onComplete: (score: number | null) => void
) {
  const onStartRef = useRef(onStart);
  const onCompleteRef = useRef(onComplete);
  onStartRef.current = onStart;
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const handleStart = ({ teamIds }: { teamIds: string[] }) => {
      if (teamIds.includes(teamId)) {
        onStartRef.current();
      }
    };

    const handleComplete = ({
      results,
    }: {
      results: Record<string, number | null>;
    }) => {
      if (teamId in results) {
        onCompleteRef.current(results[teamId] ?? null);
      }
    };

    adminTeamsEmitter.on("coverageBatchStart", handleStart);
    adminTeamsEmitter.on("coverageBatchComplete", handleComplete);
    return () => {
      adminTeamsEmitter.off("coverageBatchStart", handleStart);
      adminTeamsEmitter.off("coverageBatchComplete", handleComplete);
    };
  }, [teamId]);
}
